// ============================================================================
// The Live ETA cell.
//
// TWO SOURCES, and the cell says which one it is showing:
//
//   LIVE — Loginext's `eta` for the shipment right now, fetched per booking
//     after the board loads (see `getLiveEtas`). This is the same figure the
//     customer's order-tracking page shows them, so the desk and the customer
//     are reading one number.
//
//   PLANNED — `row.eta`, written by Loginext's DELIVERYPLANNING webhook when the
//     trip was confirmed. Always there, never moves. It is the fallback, shown
//     in muted ink and labelled, because presenting a trip-planning estimate as
//     a live one is exactly the confusion this column exists to avoid.
//
// A delivered shipment shows neither: it arrived, and the time it arrived is
// what matters then, not a forecast of when it would.
// ============================================================================
import { Tooltip } from "antd";
import React from "react";
import { fmtIST } from "../format";
import { LogisticsOrderRow } from "../types";
import { isDelivered } from "./row_predicates";

// Can this booking have a live ETA at all? Mirrors the backend's `_eligible`, so the page only asks
// about shipments that could answer — which is what keeps the fan-out to Loginext small.
export const canHaveLiveEta = (r: LogisticsOrderRow): boolean =>
  r.provider_type === "own_tms" &&
  !!r.awb_no &&
  !isDelivered(r) &&
  r.delivery_status !== "cancelled";

interface Props {
  row: LogisticsOrderRow;
  // booking id → ISO ETA, or null when Loginext was asked and had none. Absent means not yet
  // fetched (or never eligible) — which is why this is not simply `string | null`.
  live: Record<number, string | null>;
  loading: boolean;
  // The lookup failed. Kept separate from an empty map: "we could not ask" and "LogiNext had
  // nothing" look identical in the data and must not read identically to an operator.
  failed: boolean;
}

const LiveEtaCell: React.FC<Props> = ({ row, live, loading, failed }) => {
  if (isDelivered(row)) {
    return (
      <Tooltip title={`Delivered ${fmtIST(row.delivered_at, "D MMM, h:mm A")} — no ETA to show.`}>
        <span style={{ color: "#bbb", cursor: "help" }}>—</span>
      </Tooltip>
    );
  }

  const liveAt = live[row.id];
  if (liveAt) {
    return (
      <Tooltip title="Live from LogiNext — the same ETA the customer sees on their tracking page.">
        <span style={{ whiteSpace: "nowrap", cursor: "help" }}>{fmtIST(liveAt, "D MMM, h:mm A")}</span>
      </Tooltip>
    );
  }

  // Nothing live. WHY there is nothing decides the wording — `asked` is the difference between
  // LogiNext answering "no ETA" and us never getting an answer at all. The booking is in the map
  // (with a null) only when the server asked LogiNext and it came back empty.
  const asked = Object.prototype.hasOwnProperty.call(live, row.id);
  const whyNoLive = loading
    ? "Still checking LogiNext for a live one."
    : failed
    ? "Could not reach the live-ETA service, so no live figure was fetched — this is not LogiNext saying it has none."
    : asked
    ? "LogiNext has no live ETA for this shipment right now."
    : canHaveLiveEta(row)
    ? "No live ETA was fetched for this shipment."
    : "This shipment is not on a LogiNext truck, so it has no live ETA.";

  if (row.eta) {
    return (
      <Tooltip title={`Planned ETA, set when the trip was confirmed. ${whyNoLive}`}>
        <span style={{ whiteSpace: "nowrap", color: "#8c8c8c", cursor: "help" }}>
          {fmtIST(row.eta, "D MMM, h:mm A")}
          <span style={{ fontSize: 10, marginLeft: 4 }}>planned</span>
        </span>
      </Tooltip>
    );
  }

  // No live ETA and no planned one either — first-mile pickups have no planned ETA at all, since
  // the trip-planning webhook only writes one for DELIVERY stops.
  return (
    <Tooltip title={`No ETA on this shipment. ${whyNoLive}`}>
      <span style={{ color: "#bbb", cursor: "help" }}>—</span>
    </Tooltip>
  );
};

export default LiveEtaCell;
